import { useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { pickChannel } from './channels';
import { connectSocket } from './socket';
import { useDisplayStore } from './store';

const HEARTBEAT_MS = 5_000;

export function PairedScreen() {
  const display = useDisplayStore((s) => s.display);
  const setDisplay = useDisplayStore((s) => s.setDisplay);
  const setLiveSnapshot = useDisplayStore((s) => s.setLiveSnapshot);
  const clear = useDisplayStore((s) => s.clear);
  const token = useDisplayStore((s) => s.displayToken);
  const [connected, setConnected] = useState(true);

  // ─── Heartbeat poller (config refresh) ───
  useEffect(() => {
    let cancelled = false;

    async function beat() {
      try {
        const updated = await api.heartbeat();
        if (!cancelled) {
          setDisplay(updated);
          setConnected(true);
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          clear();
          return;
        }
        if (!cancelled) setConnected(false);
      }
    }

    beat();
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [setDisplay, clear]);

  // ─── Socket.IO live-game subscription ───
  useEffect(() => {
    if (!token) return;
    const socket = connectSocket(token, {
      onGameState: (snapshot) => {
        setLiveSnapshot(snapshot);
      },
      onDisplayConfig: () => {
        // Backend pushed a config refresh — no-op here; the heartbeat
        // loop refetches the canonical DisplayPublic shape in <=5s.
      },
      onConnect: () => setConnected(true),
      onDisconnect: () => setConnected(false),
    });
    return () => {
      socket.disconnect();
    };
  }, [token, setLiveSnapshot]);

  // Reset live snapshot if the admin switches the assigned game (or
  // unassigns it entirely) — otherwise stale numbers + rosters stick.
  const assignedGameId = (display?.displayConfig as {
    assignedGameId?: string;
  } | null | undefined)?.assignedGameId;
  useEffect(() => {
    if (!assignedGameId) setLiveSnapshot(null);
  }, [assignedGameId, setLiveSnapshot]);

  if (!display) {
    return (
      <div className="flex min-h-screen items-center justify-center text-white/40">
        Loading…
      </div>
    );
  }

  const ChannelComponent = pickChannel(display.channelType);
  return (
    <ChannelComponent
      display={display}
      connected={connected}
      onUnpair={clear}
    />
  );
}
